import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellRing, BellOff, Smartphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const PushEnable = ({ subscribePath = "/my/push/subscribe", unsubscribePath = "/my/push/unsubscribe", label = "Get a pop-up on your phone for new jobs, reminders & the night before." }) => {
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const isStandalone = typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);
  const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  const [status, setStatus] = useState("checking"); // checking | on | off | blocked | unsupported
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) { setStatus("unsupported"); return; }
    if (Notification.permission === "denied") { setStatus("blocked"); return; }
    navigator.serviceWorker.getRegistration().then(async (reg) => {
      if (!reg) { setStatus("off"); return; }
      const sub = await reg.pushManager.getSubscription();
      setStatus(sub ? "on" : "off");
    }).catch(() => setStatus("off"));
  }, [supported]);

  const enable = async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setStatus(perm === "denied" ? "blocked" : "off"); return; }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      if (!data.key) { toast.error("Alerts not configured yet."); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      await axios.post(`${API}${subscribePath}`, { subscription: sub.toJSON() });
      setStatus("on");
      toast.success("Job alerts enabled on this phone 🎉");
    } catch (e) {
      toast.error("Could not enable alerts. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        await axios.post(`${API}${unsubscribePath}`, { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setStatus("off");
      toast.success("Job alerts turned off on this phone.");
    } catch (e) {
      toast.error("Could not turn off alerts.");
    } finally {
      setBusy(false);
    }
  };

  if (status === "checking") return null;

  if (status === "unsupported" && isIOS && !isStandalone) {
    return (
      <div className="mt-4 flex items-start gap-3 rounded-2xl border border-blaze/40 bg-blue-50 p-4" data-testid="push-ios-hint">
        <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-blaze" />
        <div>
          <p className="text-sm font-bold text-slate-900">Turn on job alerts</p>
          <p className="mt-0.5 text-xs text-slate-600">On iPhone: tap the <strong>Share</strong> icon in Safari, choose <strong>Add to Home Screen</strong>, then open MoversNearby from your home screen to enable alerts.</p>
        </div>
      </div>
    );
  }

  if (status === "unsupported") return null;

  if (status === "blocked") {
    return (
      <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-400 bg-amber-50 p-4" data-testid="push-blocked">
        <BellOff className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <p className="text-sm font-bold text-slate-900">Alerts are blocked</p>
          <p className="mt-0.5 text-xs text-slate-600">Enable notifications for this site in your browser settings to get job alerts on your phone.</p>
        </div>
      </div>
    );
  }

  if (status === "on") {
    return (
      <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-4" data-testid="push-enabled">
        <div className="flex items-center gap-3">
          <BellRing className="h-5 w-5 shrink-0 text-emerald-600" />
          <div>
            <p className="text-sm font-bold text-slate-900">Job alerts are on</p>
            <p className="mt-0.5 text-xs text-slate-600">You'll get a pop-up for new jobs &amp; reminders on this phone.</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button onClick={disable} disabled={busy} className="rounded-lg border border-slate-300 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 disabled:opacity-50" data-testid="push-disable-btn">Turn off</button>
        </div>
      </div>
    );
  }
  return (
    <button onClick={enable} disabled={busy} className="mt-4 flex w-full items-center justify-between gap-3 rounded-2xl border border-blaze/40 bg-blue-50 p-4 text-left transition-colors active:scale-[0.99] disabled:opacity-50" data-testid="push-enable-btn">
      <div className="flex items-center gap-3">
        <Bell className="h-5 w-5 shrink-0 text-blaze" />
        <div>
          <p className="text-sm font-bold text-slate-900">Turn on job alerts</p>
          <p className="mt-0.5 text-xs text-slate-600">{label}</p>
        </div>
      </div>
      <span className="shrink-0 rounded-lg bg-blaze px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-white">{busy ? "…" : "Enable"}</span>
    </button>
  );
};
